/**
 * OpenAI 兼容适配器（T03）。
 *
 * 覆盖所有暴露 `/v1/chat/completions` 与 `/v1/models` 的服务：
 * OpenAI 官方、OpenRouter、DeepSeek、Moonshot、vLLM、Ollama 的 OpenAI 兼容层、
 * 自建网关等。**不使用任何 SDK，只用原生 `fetch`。**
 *
 * 关键行为：
 * - 流式：`fetch` + `ReadableStream` 解析 SSE，支持跨 chunk 半行拼接与 `[DONE]`；
 * - 非流式：一次性返回文本；
 * - `AbortSignal` 取消：中止请求并**静默结束迭代**（不产出任何分片，含 error）；
 * - HTTP 错误 / 超时 / 网络错误：规范化为 `StreamChunk{type:'error'}`，不抛异常；
 * - 流在 `[DONE]` / 内联 error / 取消时提前结束时，主动释放底层 response body，
 *   不留悬挂连接；
 * - `delta.reasoning_content`（DeepSeek / 兼容网关的思考内容）映射为 `type:'reasoning'`；
 * - `listModels()` 调 `/v1/models`。
 */

import {
  chatCompletionsUrl,
  clampRetries,
  clampRetryDelayMs,
  clampTimeoutMs,
  isAbortError,
  jsonHeaders,
  modelsUrl,
  openResponseWithRetry,
  ProviderHttpError,
  readJsonBody,
  readSSEData,
  resolveDeps,
  streamHeaders,
  toProviderError,
  type OpenResponseResult,
} from './http';
import type {
  ChatRequest,
  FetchLike,
  ProviderAdapter,
  ProviderDeps,
  ResolvedProviderConfig,
  StreamChunk,
  StreamUsage,
  ToolSpec,
} from './types';

export const OPENAI_KIND = 'openai' as const;
const COMPATIBILITY_STORAGE_KEY = 'zxtalk.openaiCompatibilityProfiles.v2';
const compatibilityProfiles = new Map<string, number>();
let compatibilityProfilesLoaded = false;

function compatibilityKey(baseUrl: string, model: string): string {
  return `${baseUrl.trim()}\u0000${model.trim()}`;
}

function loadCompatibilityProfiles(): void {
  if (compatibilityProfilesLoaded) return;
  compatibilityProfilesLoaded = true;
  try {
    const raw = globalThis.localStorage?.getItem(COMPATIBILITY_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    for (const [key, value] of Object.entries(parsed)) {
      if (Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 3) {
        compatibilityProfiles.set(key, Number(value));
      }
    }
  } catch {
    // localStorage 在隐私模式/SSR 中可能不可用；内存缓存仍然有效。
  }
}

function rememberCompatibilityProfile(key: string, index: number): void {
  loadCompatibilityProfiles();
  compatibilityProfiles.set(key, index);
  try {
    const entries = [...compatibilityProfiles.entries()].slice(-100);
    globalThis.localStorage?.setItem(COMPATIBILITY_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Best effort only; never let a storage failure break a provider request.
  }
}

/** 思考等级 -> OpenAI 兼容的 `reasoning_effort`。`off` 不下发该字段。 */
const REASONING_EFFORT: Record<string, string | undefined> = {
  off: undefined,
  low: 'low',
  medium: 'medium',
  high: 'high',
};

export type OpenAITokenField = 'max_tokens' | 'max_completion_tokens' | 'none';

export interface OpenAIChatBodyOptions {
  /** 关闭部分兼容网关不接受的采样/思考参数。 */
  includeSampling?: boolean;
  /** 不同 OpenAI 兼容网关使用的输出 token 字段。 */
  tokenField?: OpenAITokenField;
  includeReasoning?: boolean;
}

export interface OpenAIChatRequestBody {
  model: string;
  messages: Record<string, unknown>[];
  stream: boolean;
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
  reasoning_effort?: string;
  tools?: Record<string, unknown>[];
  [key: string]: unknown;
}

/** 把内部 ChatMessage 转成 OpenAI 消息格式（含多模态图片与工具结果）。 */
export function toOpenAIMessages(req: ChatRequest): Record<string, unknown>[] {
  const messages: Record<string, unknown>[] = [];

  const systemPrompt = req.settings.systemPrompt;
  const hasUserSystemPrompt = req.messages.some(
    (message) => message.role === 'system' && message.content.trim() !== '',
  );
  if (!hasUserSystemPrompt && systemPrompt?.trim()) {
    messages.push({ role: 'system', content: systemPrompt });
  }

  for (const message of req.messages) {
    if (message.role === 'tool') {
      messages.push({ role: 'tool', content: message.content });
      continue;
    }

    const images = message.images ?? [];
    if (images.length > 0 && message.role === 'user') {
      const parts: Record<string, unknown>[] = [];
      if (message.content) parts.push({ type: 'text', text: message.content });
      for (const url of images) {
        parts.push({ type: 'image_url', image_url: { url } });
      }
      messages.push({ role: message.role, content: parts });
      continue;
    }

    messages.push({ role: message.role, content: message.content });
  }

  return messages;
}

/** 把内部 ToolSpec 转成 OpenAI 的 `tools` 字段。 */
export function toOpenAITools(tools?: ToolSpec[]): Record<string, unknown>[] | undefined {
  if (!tools || tools.length === 0) return undefined;
  return tools.map((tool) => ({
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.inputSchema,
    },
  }));
}

/** 组装请求体；参数按 ModelSettings 映射。 */
export function buildChatBody(
  req: ChatRequest,
  options: OpenAIChatBodyOptions = {},
): OpenAIChatRequestBody {
  const includeSampling = options.includeSampling ?? true;
  const tokenField = options.tokenField ?? 'max_tokens';
  const body: OpenAIChatRequestBody = {
    model: req.model,
    messages: toOpenAIMessages(req),
    stream: req.settings.stream,
  };

  if (includeSampling) {
    body.temperature = req.settings.temperature;
    body.top_p = req.settings.topP;
  }
  if (tokenField !== 'none') body[tokenField] = req.settings.maxOutputTokens;

  const effort = REASONING_EFFORT[req.settings.thinkingLevel];
  if ((options.includeReasoning ?? true) && effort) body.reasoning_effort = effort;

  const tools = toOpenAITools(req.tools);
  if (tools) body.tools = tools;

  return body;
}

/**
 * 常见国产 OpenAI 兼容网关的请求参数并不完全一致。
 * 这些候选只在服务端明确拒绝 400/422 时依次尝试，避免正常请求重复计费。
 */
export function buildCompatibilityBodies(req: ChatRequest): OpenAIChatRequestBody[] {
  const candidates = [
    buildChatBody(req),
    buildChatBody(req, { includeSampling: false, includeReasoning: false }),
    buildChatBody(req, {
      includeSampling: false,
      includeReasoning: false,
      tokenField: 'max_completion_tokens',
    }),
    buildChatBody(req, { includeSampling: false, includeReasoning: false, tokenField: 'none' }),
  ];
  // Stable positions are persisted as capability profiles; never renumber by request shape.
  return candidates;
}

interface ChatCompletionDelta {
  content?: string | null;
  reasoning_content?: string | null;
  reasoning?: string | null;
  tool_calls?: {
    index?: number;
    id?: string;
    function?: { name?: string; arguments?: string };
  }[];
}

interface ChatCompletionShape {
  choices?: {
    delta?: ChatCompletionDelta;
    message?: ChatCompletionDelta;
    finish_reason?: string | null;
  }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number } | null;
  error?: { message?: string; type?: string } | null;
}

export interface ParsedStreamPayload {
  chunks: StreamChunk[];
  usage?: StreamUsage;
}

function toUsage(usage: ChatCompletionShape['usage']): StreamUsage | undefined {
  if (!usage) return undefined;
  return {
    promptTokens: usage.prompt_tokens ?? 0,
    completionTokens: usage.completion_tokens ?? 0,
  };
}

/**
 * 解析一条 SSE `data:` 负载为若干 StreamChunk。
 *
 * `[DONE]` 与 usage 的收尾由调用方处理，这里只产出内容增量，
 * 保证一次回答只会有一个 `done` 分片。
 */
export function mapStreamPayload(payload: string): ParsedStreamPayload {
  const trimmed = payload.trim();
  if (!trimmed || trimmed === '[DONE]') return { chunks: [] };

  let parsed: ChatCompletionShape;
  try {
    parsed = JSON.parse(trimmed) as ChatCompletionShape;
  } catch {
    // 单个坏分片不应中断整个回答，跳过即可。
    return { chunks: [] };
  }

  if (parsed.error) {
    return {
      chunks: [{ type: 'error', error: parsed.error.message ?? 'Provider 返回错误', code: 'http' }],
    };
  }

  const chunks: StreamChunk[] = [];

  for (const choice of parsed.choices ?? []) {
    const delta = choice.delta ?? choice.message;
    if (!delta) continue;

    const reasoning = delta.reasoning_content ?? delta.reasoning;
    if (typeof reasoning === 'string' && reasoning.length > 0) {
      chunks.push({ type: 'reasoning', delta: reasoning });
    }

    if (typeof delta.content === 'string' && delta.content.length > 0) {
      chunks.push({ type: 'text', delta: delta.content });
    }

    for (const call of delta.tool_calls ?? []) {
      chunks.push({
        type: 'tool_call',
        delta: JSON.stringify({
          index: call.index ?? 0,
          id: call.id,
          name: call.function?.name,
          arguments: call.function?.arguments ?? '',
        }),
      });
    }
  }

  const usage = toUsage(parsed.usage);
  return usage ? { chunks, usage } : { chunks };
}

/** 非流式响应 -> StreamChunk[]（文本 + 恰好一个 done）。 */
export function mapCompletionResponse(json: unknown): StreamChunk[] {
  const parsed = json as ChatCompletionShape | null;
  if (parsed?.error) {
    return [{ type: 'error', error: parsed.error.message ?? 'Provider 返回错误', code: 'http' }];
  }

  const chunks: StreamChunk[] = [];
  const message = parsed?.choices?.[0]?.message;

  const reasoning = message?.reasoning_content ?? message?.reasoning;
  if (typeof reasoning === 'string' && reasoning.length > 0) {
    chunks.push({ type: 'reasoning', delta: reasoning });
  }

  chunks.push({ type: 'text', delta: typeof message?.content === 'string' ? message.content : '' });

  for (const call of message?.tool_calls ?? []) {
    chunks.push({
      type: 'tool_call',
      delta: JSON.stringify({
        index: call.index ?? 0,
        id: call.id,
        name: call.function?.name,
        arguments: call.function?.arguments ?? '',
      }),
    });
  }

  const usage = toUsage(parsed?.usage);
  chunks.push(usage ? { type: 'done', usage } : { type: 'done' });
  return chunks;
}

function errorChunk(error: unknown): StreamChunk {
  const normalized = toProviderError(error);
  return {
    type: 'error',
    error: normalized.message,
    code: normalized.code,
    status: normalized.status,
  };
}

function canRetryCompatibility(error: unknown): boolean {
  if (!(error instanceof ProviderHttpError) || (error.status !== 400 && error.status !== 422)) return false;
  const message = error.message.toLowerCase();
  // 模型不存在/无权限与参数兼容无关，继续重试只会增加延迟。
  if (
    message.includes('model_not_found') ||
    message.includes('invalid_model') ||
    message.includes('model_not_available') ||
    message.includes('当前密钥无法使用此模型') ||
    message.includes('model unavailable') ||
    /insufficient[_ ](?:credits?|quota|balance)|(?:credit|balance).{0,24}(?:too low|exhausted)|not enough credits?|余额不足|额度不足/.test(message)
  ) return false;
  return true;
}

export class OpenAICompatibleAdapter implements ProviderAdapter {
  readonly id: string;
  readonly kind = OPENAI_KIND;
  private readonly config: ResolvedProviderConfig;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly retryDelayMs: number;
  private readonly extraHeaders: Record<string, string>;
  private readonly negotiateCompatibility: boolean;

  constructor(config: ResolvedProviderConfig, deps?: ProviderDeps) {
    const resolved = resolveDeps(deps);
    this.config = config;
    this.id = config.id;
    this.fetchImpl = resolved.fetch;
    // 配置值优先，但一律过边界校验（负数 / NaN 不得绕过钳制）。
    this.timeoutMs = clampTimeoutMs(config.timeoutMs, resolved.timeoutMs);
    this.retries = clampRetries(config.retries, resolved.retries);
    this.retryDelayMs = clampRetryDelayMs(config.retryDelayMs, resolved.retryDelayMs);
    // 自定义头：ProviderConfig.headers 覆盖 ProviderDeps.extraHeaders。
    this.extraHeaders = { ...resolved.extraHeaders, ...(config.headers ?? {}) };
    this.negotiateCompatibility = deps?.negotiateCompatibility ?? true;
  }

  private headers(stream: boolean): Record<string, string> {
    return stream
      ? streamHeaders(this.config.apiKey, this.extraHeaders)
      : jsonHeaders(this.config.apiKey, this.extraHeaders);
  }

  private deps() {
    return {
      fetch: this.fetchImpl,
      timeoutMs: this.timeoutMs,
      retries: this.retries,
      retryDelayMs: this.retryDelayMs,
      extraHeaders: this.extraHeaders,
    };
  }

  async listModels(): Promise<string[]> {
    const { response, guard } = await openResponseWithRetry({
      url: modelsUrl(this.config.baseUrl),
      init: { method: 'GET', headers: this.headers(false) },
      deps: this.deps(),
    });

    try {
      const json = await readJsonBody<{ data?: { id?: string }[] }>(response);
      return (json.data ?? [])
        .map((item) => item.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
    } finally {
      guard.dispose();
    }
  }

  async *chat(req: ChatRequest): AsyncIterable<StreamChunk> {
    // 契约：取消 = 静默结束，不产出任何分片（含 error）。
    if (req.signal?.aborted) return;

    let opened: OpenResponseResult | undefined;
    let lastError: unknown;
    const bodies = buildCompatibilityBodies(req);
    // 仅对用户自定义兼容网关启用降级：官方 OpenAI 的 400 应保持原始语义，
    // 避免对明确的参数错误额外发起请求。
    let customEndpoint = false;
    try { customEndpoint = new URL(this.config.baseUrl).hostname !== 'api.openai.com'; }
    catch { /* Existing URL validation below returns the provider config error. */ }
    const canFallback = this.negotiateCompatibility && (this.config.kind === 'custom' || customEndpoint);
    const profileKey = compatibilityKey(this.config.baseUrl, req.model);
    loadCompatibilityProfiles();
    const preferred = canFallback ? compatibilityProfiles.get(profileKey) : undefined;
    const order = !canFallback ? [0] : preferred === undefined
      ? bodies.map((_, index) => index)
      : [preferred, ...bodies.map((_, index) => index).filter(index => index !== preferred)];
    const tried = new Set<string>();
    for (const index of order) {
      if (req.signal?.aborted) return;
      const body = JSON.stringify(bodies[index]);
      if (tried.has(body)) continue;
      tried.add(body);
      try {
        opened = await openResponseWithRetry({
          url: chatCompletionsUrl(this.config.baseUrl),
          init: {
            method: 'POST',
            headers: this.headers(req.settings.stream),
            body,
          },
          deps: this.deps(),
          externalSignal: req.signal,
        });
        if (canFallback) rememberCompatibilityProfile(profileKey, index);
        break;
      } catch (error) {
        lastError = error;
        const shouldFallback = canFallback && canRetryCompatibility(error);
        if (!shouldFallback) break;
      }
    }
    if (!opened) {
      if (req.signal?.aborted) return;
      yield errorChunk(lastError);
      return;
    }

    const { response, guard } = opened;
    // 是否「提前」结束读取。提前结束时服务端可能仍在 keep-alive 保持连接，
    // 必须主动中止底层 fetch，否则 response body 悬挂、连接池被占满。
    let earlyExit = false;

    try {
      if (!req.settings.stream || /^application\/json(?:;|$)/i.test(response.headers.get('content-type') ?? '')) {
        const json = await readJsonBody(response);
        for (const chunk of mapCompletionResponse(json)) yield chunk;
        return;
      }

      if (!response.body) {
        earlyExit = true;
        yield { type: 'error', error: '响应没有可读流', code: 'parse' };
        return;
      }

      let usage: StreamUsage | undefined;
      for await (const payload of readSSEData(response.body, guard.signal)) {
        if (req.signal?.aborted) {
          earlyExit = true;
          return;
        }
        guard.reset();
        if (payload.trim() === '[DONE]') {
          // 服务端发完 [DONE] 常常不关连接；这里提前退出并释放 body。
          earlyExit = true;
          break;
        }
        const parsed = mapStreamPayload(payload);
        if (parsed.usage) usage = parsed.usage;
        for (const chunk of parsed.chunks) {
          if (chunk.type === 'error') {
            earlyExit = true;
            yield chunk;
            return;
          }
          yield chunk;
        }
      }

      if (req.signal?.aborted) {
        earlyExit = true;
        return;
      }
      if (guard.timedOut()) {
        earlyExit = true;
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }

      // 无论服务端是否发送 [DONE]，都在结束时补一次 done，保证上层收尾一致。
      yield usage ? { type: 'done', usage } : { type: 'done' };
    } catch (error) {
      earlyExit = true;
      if (req.signal?.aborted) return;
      if (guard.timedOut()) {
        yield { type: 'error', error: `请求超时（${this.timeoutMs}ms）`, code: 'timeout' };
        return;
      }
      if (isAbortError(error)) return;
      yield errorChunk(error);
    } finally {
      if (earlyExit) guard.abort();
      guard.dispose();
    }
  }
}

export function createOpenAIAdapter(
  config: ResolvedProviderConfig,
  deps?: ProviderDeps,
): ProviderAdapter {
  return new OpenAICompatibleAdapter(config, deps);
}

export { ProviderHttpError };
